import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import transparentBookmarkIcon from "../../../assets/transparent-bookmark.svg";
import { trpc } from "../../../utils/trpc";
import { PrimaryButton, TertiaryButton } from "../buttons";
import { FlexColContainer, FlexRowContainer } from "../layout";
import { Modal } from "../Modal";
import { BookmarkIcon } from "./BookmarkIcon";
import { buildChapterGroups, type BookmarkItem } from "./utils";
import { useUser } from "../../hooks/useUser";

export type { BookmarkItem };
interface BookmarkSectionProps {
  bookmarks: BookmarkItem[];
  collectionId: number;
  collectionColour: string;
  publicationFilename: string;
  publicationTitle: string;
}

const SectionHeading = styled.div<{ level: 0 | 1 | 2 }>`
  background-color: ${({ theme, level }) =>
    level === 0
      ? theme.colours.uIGrey.light._10
      : level === 1
        ? theme.colours.uIGrey.light._15
        : theme.colours.uIGrey.light._20};
  color: ${({ theme }) => theme.colours.accent.dark._50};
  font-size: 10px;
  font-weight: 500;
  height: 18px;
  padding: 1px 0 0 ${({ level }) => 6 + level * 10}px;
`;

const BookmarkGroup = ({
  children,
  level = 0,
}: {
  children: React.ReactNode;
  level?: 0 | 1 | 2;
}) => (
  <div
    css={(theme) => css`
      border-left: ${level > 0
        ? `16px solid ${level === 1 ? theme.colours.uIGrey.light._15 : theme.colours.uIGrey.light._20}`
        : "none"};
    `}
  >
    {children}
  </div>
);

const BookmarkList = ({ children }: { children: React.ReactNode }) => (
  <FlexColContainer
    css={css`
      gap: 16px;
      padding: 12px;
    `}
  >
    {children}
  </FlexColContainer>
);

export const BookmarkSection = ({
  bookmarks,
  collectionId,
  collectionColour,
  publicationFilename,
  publicationTitle,
}: BookmarkSectionProps) => {
  const [bookmarkToDelete, setBookmarkToDelete] = useState<BookmarkItem | null>(
    null,
  );
  const { user } = useUser();
  const utils = trpc.useUtils();

  const removeBookmark = trpc.removeBookmark.useMutation({
    onSuccess: async () => {
      setBookmarkToDelete(null);
      await utils.getBookmarksByCollection.invalidate({ collectionId });
    },
  });

  const chapterGroups = useMemo(
    () => buildChapterGroups(bookmarks, publicationTitle),
    [bookmarks, publicationTitle],
  );

  const renderBookmark = (bookmark: BookmarkItem) => {
    const linkPathname = publicationFilename
      ? `/pub/${publicationFilename}`
      : bookmark.articleSlug
        ? `/article/${bookmark.articleSlug}`
        : undefined;
    const frjArticleWithoutAccess =
      Boolean(bookmark.articleSlug) && !user?.frjAccess;

    return (
      <div
        key={bookmark.id}
        css={(theme) => css`
          background-color: ${theme.colours.uIGrey.light._5};
          border: 2px solid ${theme.colours.uIGrey.light._30};
          border-radius: 8px;
          box-shadow:
            0 1px 4px 0 rgba(9, 9, 11, 0.08),
            inset 0 0 4px 2px ${theme.colours.uIGrey.light._20};
          padding: 4px;
        `}
      >
        <FlexRowContainer
          css={(theme) => css`
            background-image: linear-gradient(
              to bottom,
              ${theme.colours.accent.light._30},
              ${theme.colours.accent.light._10}
            );
            border: 1px solid ${theme.colours.accent.light._10};
            border-radius: 2px;
            height: 32px;
            justify-content: flex-start;
            padding: 0 8px 0 4px;
          `}
        >
          <button
            css={css`
              align-items: center;
              background: none;
              border: none;
              cursor: pointer;
              display: flex;
              height: 16px;
              justify-content: center;
              margin-left: 2px;
              padding: 0;
              position: relative;
              width: 16px;
            `}
            onClick={() => setBookmarkToDelete(bookmark)}
            aria-label="Remove bookmark"
          >
            <span
              css={css`
                position: absolute;
                inset: 0;
                button:hover > & {
                  opacity: 0;
                }
              `}
            >
              <BookmarkIcon colour={collectionColour} />
            </span>
            <img
              src={transparentBookmarkIcon}
              alt="Remove bookmark"
              css={css`
                height: 16px;
                width: 16px;
                opacity: 0;
                button:hover > & {
                  opacity: 1;
                }
              `}
            />
          </button>
          {linkPathname && !frjArticleWithoutAccess ? (
            <Link
              to={{
                pathname: linkPathname,
                hash:
                  publicationFilename || bookmark.articleSlug
                    ? bookmark.sectionId
                    : undefined,
              }}
              css={(theme) => css`
                color: ${theme.colours.primary.dark._30};
                font-family: ${theme.customTypography.book.label._3.fontFamily};
                font-size: 14px;
                overflow: hidden;
                text-decoration: none;
                text-overflow: ellipsis;
                white-space: nowrap;
                &:hover {
                  text-decoration: underline;
                }
              `}
            >
              {bookmark.sectionTitle}
            </Link>
          ) : (
            <div
              css={(theme) => css`
                color: ${theme.colours.primary.dark._30};
                font-family: ${theme.customTypography.book.label._3.fontFamily};
                font-size: 14px;
                overflow: hidden;
                text-decoration: none;
                text-overflow: ellipsis;
                white-space: nowrap;
                &:hover {
                  text-decoration: underline;
                }
              `}
            >
              {bookmark.sectionTitle}
            </div>
          )}
        </FlexRowContainer>
      </div>
    );
  };

  return (
    <>
      {chapterGroups.map((chapter) => (
        <div key={chapter.chapterTitle}>
          <SectionHeading level={0}>{chapter.chapterTitle}</SectionHeading>

          {chapter.entries.map((entry, index) =>
            entry.type === "bookmark_group" ? (
              <BookmarkList key={`chapter-group-${index}`}>
                {entry.bookmarks.map((b) => renderBookmark(b))}
              </BookmarkList>
            ) : (
              <BookmarkGroup key={entry.section.sectionTitle} level={1}>
                <SectionHeading level={1}>
                  {entry.section.sectionTitle}
                </SectionHeading>

                {entry.section.entries.map((sEntry, sIndex) =>
                  sEntry.type === "bookmark_group" ? (
                    <BookmarkList key={`section-group-${sIndex}`}>
                      {sEntry.bookmarks.map((b) => renderBookmark(b))}
                    </BookmarkList>
                  ) : (
                    <BookmarkGroup
                      key={sEntry.subSection.subSectionTitle}
                      level={2}
                    >
                      <SectionHeading level={2}>
                        {sEntry.subSection.subSectionTitle}
                      </SectionHeading>
                      <BookmarkList key={`subsection-group-${sIndex}`}>
                        {sEntry.subSection.bookmarks.map((b) =>
                          renderBookmark(b),
                        )}
                      </BookmarkList>
                    </BookmarkGroup>
                  ),
                )}
              </BookmarkGroup>
            ),
          )}
        </div>
      ))}

      <Modal
        isOpen={bookmarkToDelete !== null}
        onClose={() => setBookmarkToDelete(null)}
        renderButton={() => null}
        header={<h2>Delete Bookmark</h2>}
        content={<p>Are you sure you want to delete this bookmark?</p>}
        footer={
          <>
            <TertiaryButton onClick={() => setBookmarkToDelete(null)}>
              Cancel
            </TertiaryButton>
            <PrimaryButton
              onClick={() =>
                bookmarkToDelete &&
                removeBookmark.mutate({
                  collectionId,
                  bookmarkId: bookmarkToDelete.id,
                })
              }
              disabled={removeBookmark.isLoading}
            >
              {removeBookmark.isLoading ? "Deleting..." : "Delete"}
            </PrimaryButton>
          </>
        }
      />
    </>
  );
};
